import type { ReactNode } from 'react';
import { formatEuro, formatEuroExact, formatPercent } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Button, TextLink } from '../../../ds/components';
import { RateField } from '../dialogs/RateField';
import type { FinancingRecord } from '../types';
import { StagePill } from '../ui/StagePill';
import styles from './record.module.css';

/** Sidebar cards of the financing record for the team (Finanzierungen 06/07/08). */

export function SideCard({
  label,
  note,
  children,
}: {
  label: string;
  note?: ReactNode | undefined;
  children: ReactNode;
}) {
  return (
    <section className={styles.card} aria-label={label}>
      <div className={styles.eyebrowRow}>
        <div className={styles.eyebrow}>{label}</div>
        {note ? <span className={styles.eyebrowNote}>{note}</span> : null}
      </div>
      {children}
    </section>
  );
}

function LinkRow({
  eyebrow,
  value,
  sub,
  action,
}: {
  eyebrow: string;
  value: ReactNode;
  sub?: ReactNode | undefined;
  action?: ReactNode | undefined;
}) {
  return (
    <div className={styles.linkRow}>
      <div className={styles.linkText}>
        <div className={styles.eyebrow}>{eyebrow}</div>
        <div className={styles.linkValue}>{value}</div>
        {sub ? <div className={styles.linkSub}>{sub}</div> : null}
      </div>
      {action}
    </div>
  );
}

/** „Verknüpft": Kontakt · Deal (or „Kein Deal" with „Verknüpfen") · Einheit · „Im selben Deal". */
export function LinkedCard({
  financing,
  onLinkDeal,
}: {
  financing: FinancingRecord;
  onLinkDeal: (() => void) | null;
}) {
  const { contact, deal, noDeal, unit, sameDeal } = financing.linked;
  return (
    <SideCard label="Verknüpft">
      <div className={styles.linkList}>
        <LinkRow
          eyebrow="Kontakt"
          value={contact.name}
          sub={contact.line}
          action={
            <TextLink to={`/contacts/${contact.id}`} aria-label={`Kontakt ${contact.name} öffnen`}>
              öffnen
            </TextLink>
          }
        />
        {deal ? (
          <LinkRow
            eyebrow="Deal"
            value={deal.line}
            sub={deal.stageLine}
            action={
              <TextLink to={`/deals/${deal.id}`} aria-label={`Deal ${deal.line} öffnen`}>
                öffnen
              </TextLink>
            }
          />
        ) : null}
        {noDeal ? (
          <LinkRow
            eyebrow="Deal"
            value={noDeal.title}
            sub={noDeal.text}
            action={
              onLinkDeal ? (
                <Button variant="secondary" size="small" onClick={onLinkDeal}>
                  Verknüpfen
                </Button>
              ) : undefined
            }
          />
        ) : null}
        {unit && deal ? (
          <LinkRow
            eyebrow="Einheit"
            value={unit.line}
            sub={unit.statusLabel}
            action={
              <TextLink to={`/deals/${deal.id}?tab=units`} aria-label={`Einheit ${unit.line} öffnen`}>
                öffnen
              </TextLink>
            }
          />
        ) : null}
        {sameDeal.map((other) => (
          <LinkRow
            key={other.id}
            eyebrow="Im selben Deal"
            value={`${other.numberLabel} · ${other.typeLabel}${other.amountCents === null ? '' : ` · ${formatEuro(other.amountCents)}`}`}
            sub={<StagePill stage={other.stage} />}
            action={
              <TextLink to={`/financing/${other.id}`} aria-label={`${other.numberLabel} öffnen`}>
                öffnen
              </TextLink>
            }
          />
        ))}
      </div>
    </SideCard>
  );
}

/**
 * „Provision" (Finanzierungen 06/08): total, „0,80 % von 403.000 €", the share rows, the organization's rest, the
 * note; the due label in the eyebrow; the assigned financier edits the rate inline (`actions.editRate`).
 */
export function CommissionCard({
  financing,
  onChanged,
}: {
  financing: FinancingRecord;
  onChanged: () => void;
}) {
  const card = financing.commission;
  if (!card) return null;
  const base =
    card.ratePct !== null && card.baseCents !== null
      ? `${formatPercent(card.ratePct, 2)} von ${formatEuro(card.baseCents)}`
      : null;
  return (
    <SideCard label="Provision" note={card.dueLabel}>
      <div
        className={clsx(styles.commissionTotal, card.status === 'cancelled' && styles.commissionCancelled)}
      >
        {card.amountCents !== null ? (
          <span className={styles.commissionAmount}>{formatEuroExact(card.amountCents)}</span>
        ) : null}
        {base ? <span className={styles.commissionBase}>{base}</span> : null}
      </div>
      {card.shares.length > 0 || (card.organizationCents ?? 0) > 0 ? (
        <div className={styles.shares}>
          {card.shares.map((share) => (
            <div key={`${share.userId}-${share.roleLabel}`} className={styles.shareRow}>
              <span className={styles.shareName}>
                {share.name} <span className={styles.shareRole}>{share.roleLabel}</span>
              </span>
              <span className={styles.sharePct}>{formatPercent(share.sharePct, 2)}</span>
              <span className={styles.shareAmount}>{formatEuroExact(share.amountCents)}</span>
            </div>
          ))}
          {card.organizationCents !== null && card.organizationCents > 0 ? (
            <div className={styles.shareRow}>
              <span className={styles.shareName}>Organisation</span>
              <span className={styles.sharePct} />
              <span className={styles.shareAmount}>{formatEuroExact(card.organizationCents)}</span>
            </div>
          ) : null}
        </div>
      ) : null}
      <div className={styles.smallNote}>{card.note}</div>
      {financing.actions.editRate.allowed ? (
        <RateField financingId={financing.id} ratePct={card.ratePct} onSaved={onChanged} />
      ) : null}
    </SideCard>
  );
}

/** „Offene Tasks" (aus Kontakt und Deal) with „Task anlegen". */
export function OpenTasksCard({
  financing,
  onCreateTask,
}: {
  financing: FinancingRecord;
  onCreateTask: () => void;
}) {
  const note = financing.linked.deal ? 'aus Kontakt und Deal' : 'aus Kontakt';
  return (
    <SideCard label="Offene Tasks" note={note}>
      <div className={styles.linkList}>
        {financing.openTasks.length === 0 ? (
          <div className={clsx(styles.taskRow, styles.muted)}>Keine offenen Tasks.</div>
        ) : null}
        {financing.openTasks.map((task) => (
          <div key={task.id} className={styles.taskRow}>
            <span className={styles.taskBox} aria-hidden="true" />
            <span className={styles.taskText}>
              <span className={styles.taskTitle}>{task.title}</span>
              <span className={styles.taskContext}>{task.contextLine}</span>
            </span>
            <span className={styles.taskDue}>{task.dueLabel}</span>
          </div>
        ))}
      </div>
      <div className={styles.sideActions}>
        <Button variant="secondary" size="small" onClick={onCreateTask}>
          Task anlegen
        </Button>
      </div>
    </SideCard>
  );
}

/** The team sidebar: Verknüpft · Provision · Offene Tasks · Später (08). */
export function TeamSidebar({
  financing,
  onChanged,
  onLinkDeal,
  onCreateTask,
}: {
  financing: FinancingRecord;
  onChanged: () => void;
  onLinkDeal: (() => void) | null;
  onCreateTask: () => void;
}) {
  const closed = financing.stage.key === 'disbursed' || financing.status === 'lost';
  return (
    <div className={styles.side}>
      <LinkedCard financing={financing} onLinkDeal={onLinkDeal} />
      <CommissionCard financing={financing} onChanged={onChanged} />
      {closed ? null : <OpenTasksCard financing={financing} onCreateTask={onCreateTask} />}
      {financing.laterNote ? (
        <SideCard label="Später">
          <div className={styles.laterText}>{financing.laterNote}</div>
        </SideCard>
      ) : null}
    </div>
  );
}
